// src/utils/chunkReload.js (Shell)
//
// A tab still running an older build can ask for a file that is no longer there. Reloading
// once picks up the current build; a time-based guard (rather than a one-shot flag) lets a
// later deploy in the same tab session self-heal while a genuinely broken build can't loop.

import { reloadPage } from './reloadPage';

const LAST_RELOAD_KEY = 'shell:chunk-reload-at';
const MIN_RELOAD_INTERVAL_MS = 60 * 1000;

const CHUNK_ERROR_PATTERN = new RegExp([
    'Failed to fetch dynamically imported module', // Chrome
    'error loading dynamically imported module',   // Firefox
    'Importing a module script failed',            // Safari
    'Unable to preload CSS',                       // Vite
    'Failed to load script resources',             // Module Federation runtime (remoteEntry)
].join('|'), 'i');

/**
 * True when an error means a build file could not be loaded.
 * @param {unknown} error
 */
export function isChunkLoadError(error) {
    return CHUNK_ERROR_PATTERN.test(String(error?.message ?? error ?? ''));
}

/**
 * Reload the page unless this tab already did so in the last minute.
 * @returns {boolean} true when a reload was started
 */
export function reloadOnceForChunkError() {
    // Offline, the failed load is the network, not a missing build file — a reload would only
    // swap the app's own error for the browser's offline page.
    if (navigator.onLine === false) return false;
    try {
        const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0);
        if (Date.now() - last < MIN_RELOAD_INTERVAL_MS) return false;
        sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    } catch {
        // No sessionStorage means no loop guard — don't auto-reload.
        return false;
    }
    reloadPage();
    return true;
}
